import { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/contexts/AuthContext";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { toast } from "sonner";
import { ChevronRight, Send, StickyNote, Trash2 } from "lucide-react";
import { cn } from "@/lib/utils";

const db = supabase as any;
const DAY = 864e5;
const H = ({ children }: { children: React.ReactNode }) => (
  <p className="mb-2 font-display text-base text-foreground">{children}</p>
);
const fmtDate = (d: string) => new Date(d).toLocaleDateString(undefined, { month: "short", day: "numeric" });

export const NUDGE_PRESETS = [
  "Proud of your streak — keep going.",
  "Missed you yesterday. Today's a new day.",
  "Log your numbers this week.",
  "Your homework is waiting — you've got this.",
];

function Spark({ pts, label, fmt }: { pts: number[]; label: string; fmt: (n: number) => string }) {
  const w = 120, h = 48, pad = 4;
  const has = pts.length > 0;
  const series = pts.length === 1 ? [pts[0], pts[0]] : pts;
  const min = Math.min(...series), max = Math.max(...series), span = max - min || 1;
  const xy = series.map((p, i) => [pad + (i / Math.max(1, series.length - 1)) * (w - pad * 2), h - pad - ((p - min) / span) * (h - pad * 2) - (max === min ? (h - pad * 2) / 2 : 0)]);
  const line = xy.map(([x, y]) => `${x},${y}`).join(" ");
  const area = `${pad},${h - pad} ${line} ${w - pad},${h - pad}`;
  const delta = pts.length > 1 ? pts[pts.length - 1] - pts[0] : 0;
  return (
    <div className="rounded-lg border border-amber-500/25 bg-background/60 p-2">
      <div className="flex items-baseline justify-between gap-1">
        <span className="text-[11px] text-muted-foreground">{label}</span>
        {pts.length > 1 && <span className={cn("text-[10px]", delta >= 0 ? "text-gold-dark" : "text-muted-foreground")}>{delta >= 0 ? "▲" : "▼"}</span>}
      </div>
      <svg viewBox={`0 0 ${w} ${h}`} className="mt-1 h-14 w-full text-gold" preserveAspectRatio="none" aria-hidden="true">
        {[0.25, 0.5, 0.75].map((f) => <line key={f} x1={pad} x2={w - pad} y1={h * f} y2={h * f} stroke="currentColor" strokeOpacity="0.12" />)}
        <line x1={pad} x2={w - pad} y1={h - pad} y2={h - pad} stroke="currentColor" strokeOpacity="0.35" />
        {has ? (
          <>
            <polygon points={area} fill="currentColor" fillOpacity="0.12" />
            <polyline points={line} fill="none" stroke="currentColor" strokeWidth="2" strokeLinejoin="round" />
            {xy.slice(pts.length === 1 ? 1 : 0).map(([x, y], i) => <circle key={i} cx={x} cy={y} r="2.2" fill="currentColor" />)}
          </>
        ) : (
          <polyline points={`${pad},${h * 0.7} ${w * 0.35},${h * 0.55} ${w * 0.6},${h * 0.62} ${w - pad},${h * 0.4}`} fill="none" stroke="currentColor" strokeOpacity="0.25" strokeWidth="1.5" strokeDasharray="3 3" />
        )}
      </svg>
      <div className="mt-0.5 text-xs text-foreground">{has ? fmt(pts[pts.length - 1]) : <span className="text-muted-foreground">Not logged yet</span>}</div>
    </div>
  );
}

export function CoachActions({ memberId }: { memberId: string }) {
  const { user } = useAuth();
  const qc = useQueryClient();
  const [mode, setMode] = useState<"nudge" | "note" | null>(null);
  const [text, setText] = useState("");
  const send = useMutation({
    mutationFn: async () => {
      const body = text.trim();
      if (!body) throw new Error("Write something first.");
      const table = mode === "nudge" ? "coach_nudges" : "coach_notes";
      const { error } = await db.from(table).insert({ member_id: memberId, author_id: user!.id, body });
      if (error) throw error;
    },
    onSuccess: () => {
      toast.success(mode === "nudge" ? "Nudge sent" : "Note saved");
      qc.invalidateQueries({ queryKey: ["coach-extras", memberId] });
      setMode(null); setText("");
    },
    onError: (e: any) => toast.error("Couldn't save", { description: e.message }),
  });
  return (
    <>
      <div className="grid grid-cols-2 gap-2">
        <Button size="sm" onClick={() => { setMode("nudge"); setText(""); }} className="h-9 rounded-md bg-gold text-primary-foreground hover:bg-gold/90"><Send className="mr-1.5 h-4 w-4" />Nudge</Button>
        <Button size="sm" onClick={() => { setMode("note"); setText(""); }} className="h-9 rounded-md bg-gold text-primary-foreground hover:bg-gold/90"><StickyNote className="mr-1.5 h-4 w-4" />Add note</Button>
      </div>
      <Dialog open={!!mode} onOpenChange={(v) => !v && setMode(null)}>
        <DialogContent>
          <DialogHeader><DialogTitle className="font-display">{mode === "nudge" ? "Send a nudge" : "Private coach note"}</DialogTitle></DialogHeader>
          {mode === "nudge" && (
            <div className="flex flex-wrap gap-2">
              {NUDGE_PRESETS.map((p) => (
                <button key={p} onClick={() => setText(p)} className={cn("rounded-full border px-3 py-1 text-xs", text === p ? "border-gold bg-gold/15" : "border-border")}>{p}</button>
              ))}
            </div>
          )}
          <Textarea value={text} onChange={(e) => setText(e.target.value)} rows={4} placeholder={mode === "nudge" ? "Or write your own…" : "Only coaches and admin will see this."} />
          <Button onClick={() => send.mutate()} disabled={send.isPending} className="bg-gold text-primary-foreground hover:bg-gold/90">{mode === "nudge" ? "Send nudge" : "Save note"}</Button>
        </DialogContent>
      </Dialog>
    </>
  );
}

export function CoachMemberExtras({ memberId }: { memberId: string }) {
  const { user } = useAuth();
  const qc = useQueryClient();
  const [hwNote, setHwNote] = useState<Record<string, string>>({});
  const { data } = useQuery({
    queryKey: ["coach-extras", memberId],
    queryFn: async () => {
      const since = new Date(Date.now() - 6 * DAY).toISOString().slice(0, 10);
      const [steps, feel, conf, metrics, income, photos, hw, notes, nudges] = await Promise.all([
        db.from("schedule_steps").select("day, status").eq("user_id", memberId).gte("day", since),
        db.from("morning_feelings").select("day, feeling").eq("user_id", memberId).gte("day", since),
        db.from("day_overrides").select("day, confirmed_at").eq("user_id", memberId).gte("day", since),
        db.from("metric_entries").select("metric, value, recorded_at").eq("user_id", memberId).order("recorded_at"),
        db.from("income_records").select("gross, pay_date, created_at").eq("user_id", memberId).order("pay_date", { nullsFirst: true }),
        db.from("progress_photos").select("photo_url, created_at, ai_body_fat_estimate").eq("user_id", memberId).order("created_at"),
        db.from("academy_homework_submissions").select("*").eq("user_id", memberId).order("created_at", { ascending: false }).limit(20),
        db.from("coach_notes").select("*").eq("member_id", memberId).order("created_at", { ascending: false }),
        db.from("coach_nudges").select("*").eq("member_id", memberId).order("created_at", { ascending: false }).limit(10),
      ]);
      const sign = async (bucket: string, raw: string | null) => {
        if (!raw) return null;
        let path = raw;
        if (raw.startsWith("http")) { const m = raw.match(new RegExp(`/${bucket}/([^?]+)`)); path = m?.[1] ? decodeURIComponent(m[1]) : ""; }
        if (!path) return raw;
        const { data: s } = await supabase.storage.from(bucket).createSignedUrl(path, 3600);
        return s?.signedUrl ?? null;
      };
      const ph = photos.data ?? [];
      const base = ph[0], latest = ph.length > 1 ? ph[ph.length - 1] : null;
      const hwRows = await Promise.all((hw.data ?? []).map(async (r: any) => ({ ...r, img: await sign("homework", r.image_path) })));
      return {
        steps: steps.data ?? [], feel: feel.data ?? [], conf: conf.data ?? [], metrics: metrics.data ?? [], income: income.data ?? [],
        base: base ? { ...base, url: await sign("progress-photos", base.photo_url) } : null,
        latest: latest ? { ...latest, url: await sign("progress-photos", latest.photo_url) } : null,
        hw: hwRows, notes: notes.data ?? [], nudges: nudges.data ?? [],
      };
    },
  });

  const grade = useMutation({
    mutationFn: async ({ id, status }: { id: string; status: "passed" | "redo" }) => {
      const { error } = await supabase.functions.invoke("grade-homework", { body: { action: "review", id, status, note: hwNote[id] ?? "" } });
      if (error) throw error;
    },
    onSuccess: (_d, v) => { toast.success(v.status === "passed" ? "Marked passed" : "Sent back to redo"); qc.invalidateQueries({ queryKey: ["coach-extras", memberId] }); },
    onError: (e: any) => toast.error("Couldn't grade", { description: e.message }),
  });
  const delNote = useMutation({
    mutationFn: async (id: string) => { const { error } = await db.from("coach_notes").delete().eq("id", id); if (error) throw error; },
    onSuccess: () => qc.invalidateQueries({ queryKey: ["coach-extras", memberId] }),
  });

  const week = Array.from({ length: 7 }, (_, i) => new Date(Date.now() - (6 - i) * DAY).toISOString().slice(0, 10));
  const usd = (n: number) => `$${Math.round(n).toLocaleString()}`;
  const series = (k: string) => (data?.metrics ?? []).filter((m: any) => m.metric === k && Date.now() - new Date(m.recorded_at).getTime() < 56 * DAY).map((m: any) => Number(m.value));
  const incomeSeries = (data?.income ?? []).map((r: any) => Number(r.gross ?? 0)).filter((n: number) => n > 0);
  const waiting = (data?.hw ?? []).filter((r: any) => !["passed", "redo"].includes(r.status) || !r.graded_at);
  const graded = (data?.hw ?? []).filter((r: any) => !waiting.includes(r));

  return (
    <div className="space-y-3">
      <section className="rounded-xl border border-amber-500/30 bg-card/80 p-3 shadow-sm">
        <H>This week</H>
        <div className="grid grid-cols-7 gap-1 text-center">
          {week.map((d) => {
            const s = (data?.steps ?? []).filter((x: any) => x.day === d);
            const done = s.filter((x: any) => x.status === "done").length, missed = s.filter((x: any) => x.status === "missed").length;
            const f = (data?.feel ?? []).find((x: any) => x.day === d)?.feeling;
            const c = (data?.conf ?? []).some((x: any) => x.day === d && x.confirmed_at);
            return (
              <div key={d} className="space-y-1">
                <div className="text-[10px] text-muted-foreground">{new Date(d + "T12:00").toLocaleDateString(undefined, { weekday: "short" })}</div>
                <div title={`${done} done · ${missed} missed`} className={cn("mx-auto h-6 w-6 rounded-full border-2", done && !missed ? "border-gold bg-gold" : done ? "border-gold bg-gold/40" : missed ? "border-destructive bg-destructive/20" : "border-border")} />
                {f && <div className="truncate text-[9px] text-muted-foreground">{f}</div>}
              </div>
            );
          })}
        </div>
        {(() => { const st = data?.steps ?? []; const done = st.filter((x: any) => x.status === "done").length, missed = st.filter((x: any) => x.status === "missed").length;
          const rows: [string, string][] = [["Steps done", String(done)], ["Steps missed", String(missed)], ["Morning check-ins", String((data?.feel ?? []).length)], ["Tomorrow confirmed", String((data?.conf ?? []).filter((x: any) => x.confirmed_at).length)]];
          return <div data-k="weekSummary" className="mt-3 divide-y divide-amber-500/15 border-t border-amber-500/15 text-sm">{rows.map(([k, v]) => <div key={k} className="flex justify-between py-1.5"><span className="text-muted-foreground">{k}</span><span className="font-medium text-foreground">{v}</span></div>)}</div>; })()}
      </section>

      <section className="rounded-xl border border-amber-500/30 bg-card/80 p-3 shadow-sm">
        <H>Trends <span className="text-sm text-muted-foreground">(last 8 weeks)</span></H>
        <div className="grid grid-cols-3 gap-2">
          <Spark label="Savings" pts={series("savings")} fmt={usd} />
          <Spark label="Debt" pts={series("debt")} fmt={usd} />
          <Spark label="Body fat" pts={series("fitness")} fmt={(n) => `${n.toFixed(1)}%`} />
        </div>
      </section>

      {data?.base && <section className="rounded-xl border border-amber-500/30 bg-card/80 p-3 shadow-sm">
        <H>Photos</H>
        {(
          <div className="grid grid-cols-2 gap-2">
            {[["Baseline", data.base], ["Latest", data.latest]].map(([l, p]: any) => (
              <div key={l} className="overflow-hidden rounded-lg border border-amber-500/25 bg-card">
                {p?.url ? <img src={p.url} alt={l} className="aspect-[3/4] w-full object-cover" /> : <div className="flex aspect-[3/4] items-center justify-center text-[11px] text-muted-foreground">No newer photo</div>}
                <div className="p-1.5 text-[11px] text-muted-foreground">{l}{p ? ` · ${fmtDate(p.created_at)}` : ""}{p?.ai_body_fat_estimate ? ` · ~${p.ai_body_fat_estimate}%` : ""}</div>
              </div>
            ))}
          </div>
        )}
      </section>}

      <section className="rounded-xl border border-amber-500/30 bg-card/80 p-3 shadow-sm">
        <H>Homework</H>
        {!data?.hw.length && <div className="flex items-center justify-between rounded-lg border border-dashed border-amber-500/30 px-3 py-2 text-sm"><span className="text-muted-foreground">No homework turned in yet</span><span className="text-[11px] text-muted-foreground">Pass / Redo appears here</span></div>}
        <div className="space-y-2">
          {waiting.map((r: any) => (
            <div key={r.id} className="space-y-2 rounded-lg border border-gold bg-gold/5 p-3 text-sm">
              <div className="flex gap-3">
                {r.img && <a href={r.img} target="_blank" rel="noreferrer"><img src={r.img} alt="" className="h-16 w-16 rounded object-cover" /></a>}
                <div className="min-w-0"><div className="font-medium">{r.assignment_id}</div><p className="text-xs text-foreground/80">{r.explanation}</p></div>
              </div>
              {r.ai_feedback && <p className="rounded bg-amber-500/10 p-2 text-xs">{r.ai_feedback}</p>}
              <Textarea rows={2} placeholder="Note to the member (optional)" value={hwNote[r.id] ?? ""} onChange={(e) => setHwNote((s) => ({ ...s, [r.id]: e.target.value }))} />
              <div className="flex gap-2">
                <Button size="sm" disabled={grade.isPending} onClick={() => grade.mutate({ id: r.id, status: "passed" })} className="bg-gold text-primary-foreground hover:bg-gold/90">Pass</Button>
                <Button size="sm" variant="outline" disabled={grade.isPending} onClick={() => grade.mutate({ id: r.id, status: "redo" })}>Redo</Button>
              </div>
            </div>
          ))}
          {graded.map((r: any) => (
            <div key={r.id} className="flex justify-between rounded-lg border border-border bg-card px-3 py-2 text-xs">
              <span>{r.assignment_id}</span><span className={r.status === "passed" ? "text-gold-dark" : "text-destructive"}>{r.status === "passed" ? "Passed" : "Redo"}</span>
            </div>
          ))}
        </div>
      </section>

      <section className="rounded-xl border border-amber-500/30 bg-card/80 p-3 shadow-sm">
        <H>Coach notes <span className="text-sm text-muted-foreground">(private)</span></H>
        {!data?.notes.length && <p className="text-xs text-muted-foreground">No notes yet.</p>}
        <div className="space-y-2">
          {(data?.notes ?? []).map((n: any) => (
            <div key={n.id} className="relative flex gap-2 border-l border-gold/60 pb-1 pl-4 text-sm before:absolute before:-left-[5px] before:top-1 before:h-2.5 before:w-2.5 before:rounded-full before:bg-gold">
              <div className="flex-1"><div className="text-[11px] text-muted-foreground">{fmtDate(n.created_at)}</div><p className="whitespace-pre-wrap">{n.body}</p></div>
              {n.author_id === user?.id && <button aria-label="Delete note" onClick={() => window.confirm("Delete this note?") && delNote.mutate(n.id)}><Trash2 className="h-4 w-4 text-muted-foreground" /></button>}
            </div>
          ))}
        </div>
      </section>

      {(() => { const n = data?.nudges?.[0]; return (
        <details className="rounded-xl border border-gold/50 bg-gold/10 px-3 py-2">
          <summary className="flex cursor-pointer list-none items-center gap-3">
            <Send className="h-4 w-4 text-gold-dark" />
            <div className="flex-1 text-sm">
              <div className="text-gold-dark">{n ? "Nudge sent" : "No nudges sent yet"}</div>
              {n && <div className="text-[11px] text-muted-foreground">{n.seen_at ? `Seen ${fmtDate(n.seen_at)}` : `Sent ${fmtDate(n.created_at)} · not seen yet`}</div>}
            </div>
            {n && <ChevronRight className="h-4 w-4 text-gold-dark" />}
          </summary>
          {(data?.nudges ?? []).map((x: any) => (
            <div key={x.id} className="mt-2 border-t border-gold/30 pt-2 text-xs">
              <p className="text-foreground">{x.body}</p>
              <p className="text-muted-foreground">Sent {fmtDate(x.created_at)} · {x.seen_at ? `Seen ${fmtDate(x.seen_at)}` : "Not seen yet"}</p>
            </div>
          ))}
        </details>
      ); })()}
    </div>
  );
}

/** Member-side: newest unseen nudge from a coach, shown atop Today's Musts. */
export function NudgeCard() {
  const { user } = useAuth();
  const qc = useQueryClient();
  const { data: n } = useQuery({
    queryKey: ["my-nudge", user?.id],
    enabled: !!user,
    queryFn: async () => {
      const { data } = await db.from("coach_nudges").select("id, body, author_id, created_at").eq("member_id", user!.id).is("seen_at", null).order("created_at", { ascending: false }).limit(1).maybeSingle();
      if (!data) return null;
      const { data: p } = await db.from("profiles").select("full_name").eq("user_id", data.author_id).maybeSingle();
      return { ...data, from: p?.full_name || "Your coach" };
    },
  });
  if (!n) return null;
  const seen = async () => {
    await db.rpc("mark_nudge_seen", { _id: n.id });
    qc.invalidateQueries({ queryKey: ["my-nudge", user?.id] });
  };
  return (
    <Dialog open onOpenChange={(o) => { if (!o) seen(); }}>
      <DialogContent className="max-w-sm border-gold bg-card">
        <DialogHeader>
          <DialogTitle className="text-[11px] font-semibold uppercase tracking-[0.2em] text-gold-dark">From your coach</DialogTitle>
        </DialogHeader>
        <p className="font-serif text-lg text-foreground">{n.body}</p>
        <div className="mt-2 flex items-center justify-between">
          <span className="text-xs text-muted-foreground">— {n.from}</span>
          <Button size="sm" className="bg-gold text-primary-foreground hover:bg-gold/90" onClick={seen}>Got it</Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
